// src/components/TaskManager.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc,
  doc,
  deleteDoc,
  orderBy
} from 'firebase/firestore';
import ConfirmDialog from './ConfirmDialog';

const TaskManager = ({ onAiHelp }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskItems, setNewTaskItems] = useState(['']);
  const [currentTask, setCurrentTask] = useState(null);
  const [newTaskItem, setNewTaskItem] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const firstTaskItemRef = useRef(null);
  const { user } = useAuth();

  // Fetch tasks on component mount
  useEffect(() => {
    if (user) {
      fetchTasks();
    }
  }, [user]);

  const fetchTasks = async () => {
    try {
      const tasksRef = collection(db, 'users', user.uid, 'tasks');
      const q = query(tasksRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedTasks = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTasks(fetchedTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleCreateTaskName = (e) => {
    if (e.key === 'Enter' && newTaskName.trim()) {
      e.preventDefault();
      firstTaskItemRef.current?.focus();
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (newTaskName.trim() && newTaskItems.some(item => item.trim())) {
      try {
        const taskItems = newTaskItems
          .filter(item => item.trim())
          .map(item => ({
            id: Date.now() + Math.random(),
            text: item,
            completed: false
          }));

        const taskRef = collection(db, 'users', user.uid, 'tasks');
        const newTask = {
          name: newTaskName,
          items: taskItems,
          timestamp: new Date().toISOString()
        };

        const docRef = await addDoc(taskRef, newTask);
        setTasks(prev => [{
          id: docRef.id,
          ...newTask
        }, ...prev]);

        setNewTaskName('');
        setNewTaskItems(['']);
        setIsCreating(false);
      } catch (error) {
        console.error('Error creating task:', error);
      }
    }
  };

  const handleTaskItemChange = (index, value) => {
    const updatedItems = [...newTaskItems];
    updatedItems[index] = value;
    setNewTaskItems(updatedItems);
    
    if (index === newTaskItems.length - 1 && value.trim()) {
      setNewTaskItems([...updatedItems, '']);
    }
  };

  const handleNewTaskItemKeyPress = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.target.value.trim()) {
        if (index === newTaskItems.length - 1) {
          setNewTaskItems([...newTaskItems, '']);
        }
      }
    }
  };

  const handleAddItemToExistingTask = async (taskId) => {
    if (newTaskItem.trim()) {
      try {
        const taskRef = doc(db, 'users', user.uid, 'tasks', taskId);
        const updatedTask = tasks.find(task => task.id === taskId);
        
        if (updatedTask) {
          const newItem = {
            id: Date.now(),
            text: newTaskItem,
            completed: false
          };
          
          const updatedItems = [...updatedTask.items, newItem];
          await updateDoc(taskRef, { items: updatedItems });

          setTasks(tasks.map(task => {
            if (task.id === taskId) {
              return {
                ...task,
                items: updatedItems
              };
            }
            return task;
          }));
        }
        setNewTaskItem('');
      } catch (error) {
        console.error('Error adding task item:', error);
      }
    }
  };

  const handleToggleComplete = async (taskId, itemId) => {
    try {
      const taskRef = doc(db, 'users', user.uid, 'tasks', taskId);
      const task = tasks.find(t => t.id === taskId);
      
      if (task) {
        const updatedItems = task.items.map(item => 
          item.id === itemId 
            ? {...item, completed: !item.completed}
            : item
        );

        await updateDoc(taskRef, { items: updatedItems });

        setTasks(tasks.map(t => {
          if (t.id === taskId) {
            return {
              ...t,
              items: updatedItems
            };
          }
          return t;
        }));
      }
    } catch (error) {
      console.error('Error toggling task completion:', error);
    }
  };

  const handleDeleteClick = (task) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'tasks', taskId));
      setTasks(tasks.filter(task => task.id !== taskId));
      setCurrentTask(null);
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  return (
    <div className="task-manager">
      <button 
        className="task-manager-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        Task List
      </button>
      
      <div className={`task-manager-expanded ${isExpanded ? 'active' : ''}`}>
        <div className="task-manager-max">
        <div className="task-manager-header">
          <h2>Your Tasks</h2>
          <button 
            onClick={() => {
              setIsExpanded(false);
              setIsCreating(false);
              setNewTaskName('');
              setNewTaskItems(['']);
            }}
            className="task-manager-close"
          >
            ×
          </button>
        </div>

        {!isCreating ? (
          <button 
            onClick={() => setIsCreating(true)}
            className="create-task-button"
          >
            + Create New Task
          </button>
        ) : (
          <form onSubmit={handleCreateTask} className="create-task-form">
            <input
              type="text"
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              onKeyDown={handleCreateTaskName}
              placeholder="Enter task name..."
              className="task-input"
              autoFocus
            />
            
            <div className="task-items-creation">
              {newTaskItems.map((item, index) => (
                <div key={index} className="task-item-input">
                  <input
                    ref={index === 0 ? firstTaskItemRef : null}
                    type="text"
                    value={item}
                    onChange={(e) => handleTaskItemChange(index, e.target.value)}
                    onKeyDown={(e) => handleNewTaskItemKeyPress(e, index)}
                    placeholder={`Task item ${index + 1}...`}
                    className="task-input"
                  />
                </div>
              ))}
            </div>

            <div className="task-form-buttons">
              <button type="submit" className="task-submit">Save Task</button>
              <button 
                type="button" 
                onClick={() => {
                  setIsCreating(false);
                  setNewTaskName('');
                  setNewTaskItems(['']);
                }}
                className="task-cancel"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="tasks-list">
          {tasks.map(task => (
            <div key={task.id} className="task-item">
              <div className="task-header">
                <div className="task-header-content">
                  <h3 onClick={() => setCurrentTask(
                    currentTask?.id === task.id ? null : task
                  )}>{task.name}</h3>
                  <button
                    onClick={() => handleDeleteClick(task)}
                    className="task-delete-button"
                  >
                    Delete
                  </button>
                </div>
                <button 
                  onClick={() => onAiHelp(task)}
                  className="ai-help-button"
                >
                  Get AI assistance with this task
                </button>
              </div>

              {currentTask?.id === task.id && (
                <div className="task-details">
                  <div className="task-items">
                    {task.items.map(item => (
                      <div key={item.id} className="task-item-row">
                        <input
                          type="checkbox"
                          checked={item.completed}
                          onChange={() => handleToggleComplete(task.id, item.id)}
                        />
                        <span className={item.completed ? 'completed' : ''}>
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="add-task-item">
                    <input
                      type="text"
                      value={newTaskItem}
                      onChange={(e) => setNewTaskItem(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddItemToExistingTask(task.id);
                        }
                      }}
                      placeholder="Add new item..."
                      className="task-input"
                    />
                    <button 
                      onClick={() => handleAddItemToExistingTask(task.id)}
                      className="add-item-button"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        </div>
      </div>

      <ConfirmDialog 
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={() => handleDeleteTask(taskToDelete?.id)}
        message={`Are you sure you want to delete "${taskToDelete?.name}"?`}
      />
    </div>
  );
};

export default TaskManager;